import React, { useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { selectDealerSelected } from "../../redux/headerSlice/selectors";
import { EntryScreen } from "app/NewAIAvatarLayout/features/MiaChat/components";
import { MiaChatInterface } from "app/NewAIAvatarLayout/features/MiaChat/components/MiaChatInterface";
import useEntryScreen from "./hooks/useEntryScreen";
import { useMiaChatInterface } from "./hooks/useMiaChatInterface";
import { PromptType } from "./types";

const MiaChatMaiq = ({ view }) => {
  const { 
    homeViewPrompts, 
    analyzeViewPrompts, 
    generateViewPrompts, 
    targetViewPrompts, 
    onMessageSend,
    chatStarted,
    initialMessage,
    resetChat
  } = useEntryScreen(view);

  // Get selected dealer to watch for changes
  const selectedDealer = useSelector(selectDealerSelected) as any;
  const currentDealerId = selectedDealer?.dealer || '';
  const previousDealerIdRef = useRef<string>('');

  // Use chat interface hook - it will only initialize when initialMessage is provided
  const {
    chatState,
    conversationId,
    isLoading,
    error,
    progressCards,
    isGeneratingEmail,
    isEmailGenerationModalOpen,
    streamingData,
    currentSection,
    generatedEmail,
    isEmailPreviewOpen,
    isSocketConnected,
    isStreamingStalled,
    handleSendMessage,
    handleOptionSelect,
    handleReset: handleChatReset,
    updateInputText,
    openEmailPreview,
    closeEmailPreview,
    openEmailGenerationProgress,
    closeEmailGenerationProgress,
  } = useMiaChatInterface(view, chatStarted ? initialMessage : undefined, resetChat);

  // Combined reset handler
  const handleReset = useCallback(() => {
    handleChatReset();
    resetChat();
  }, [handleChatReset, resetChat]);

  // Watch for dealer changes and reset chat when dealer changes
  useEffect(() => {
    // Only reset if dealer actually changed (not on initial mount)
    if (previousDealerIdRef.current && previousDealerIdRef.current !== currentDealerId) {
      handleReset();
    }
    // Update the ref with current dealer ID
    previousDealerIdRef.current = currentDealerId;
  }, [currentDealerId, handleReset]);
  
  // Helper function to handle prompt submission
  const handlePromptSubmit = (promptText: string, prompts: PromptType[]) => {
    // Find the prompt object that matches the text
    const prompt = prompts.find(p => p.text === promptText) || prompts.find(p => p.text.includes(promptText)) || { text: promptText, id: Date.now().toString() };
    onMessageSend(view || 'default', prompt);
  };

  // If chat has started, show MiaChatInterface
  if (chatStarted) {
    // Use a temporary conversationId if not yet initialized
    const activeConversationId = conversationId || 'initializing';
    
    return (
      <MiaChatInterface
        canvasEnabled={view === 'generate' || view === 'target'}
        useSplitPane={view !== 'analyze'}
        conversationId={activeConversationId}
        chatState={chatState}
        onOptionSelect={handleOptionSelect}
        onSendMessage={handleSendMessage}
        onReset={handleReset}
        updateInputText={updateInputText}
        isChatInputDisabled={chatState.is_input_restricted_to_suggestions}
        progressCards={progressCards}
        isLoading={isLoading}
        isGeneratingEmail={isGeneratingEmail}
        isEmailGenerationModalOpen={isEmailGenerationModalOpen}
        streamingData={streamingData}
        currentSection={currentSection}
        generatedEmail={generatedEmail}
        isEmailPreviewOpen={isEmailPreviewOpen}
        isSocketConnected={isSocketConnected}
        isStreamingStalled={isStreamingStalled}
        onOpenEmailPreview={openEmailPreview}
        onCloseEmailPreview={closeEmailPreview}
        onOpenEmailGenerationProgress={openEmailGenerationProgress}
        onCloseEmailGenerationProgress={closeEmailGenerationProgress}
      />
    );
  }

  // Show EntryScreen for generate, analyze, target, or default views
  switch (view) {
    case 'generate':
      return <EntryScreen 
        entryQuestion="What offer would you like to generate?" 
        prompts={generateViewPrompts()}
        onSubmit={(text) => handlePromptSubmit(text, generateViewPrompts())}
      />;
    case 'analyze':
      return <EntryScreen 
        entryQuestion="How can I help you analyze your data?" 
        prompts={analyzeViewPrompts}
        onSubmit={(text) => handlePromptSubmit(text, analyzeViewPrompts)}
      />;
    case 'target':
      return <EntryScreen 
        entryQuestion="Who would you like to target?" 
        prompts={targetViewPrompts()}
        onSubmit={(text) => handlePromptSubmit(text, targetViewPrompts())}
      />;
    default:
      return <EntryScreen 
        entryQuestion="How can I help you today?" 
        prompts={homeViewPrompts}
        onSubmit={(text) => handlePromptSubmit(text, homeViewPrompts)}
      />;
  }
};

export default MiaChatMaiq;